import React from 'react';
import styled from 'styled-components';

import BreadCrumbs from '@/components/atom/BreadCrumbs';
import Arrow from '@/components/atom/icon/Arrow/Arrow';
import ClickEventLogger from '@/components/common/ClickEventLogger';
import LinkTag from '@/components/common/LinkTag';
import ListMagazineLine from '@/components/common/communication/magazine/ListMagazineLine';
import TemplateLayout from '@/components/layouts/TemplateLayout';
import StickyAppHeader from '@/components/molecules/StickyAppHeader';
import AppInstallTopBanner from '@/components/templates/mobile/communication/Common/AppInstallTopBanner';
import MagazineSkeleton from '@/components/templates/mobile/communication/Magazine/MagazineMobileTemplate/MagazineSkeleton';
import { MAGAZINE_LOG_EVENT } from '@/constants/logs/magazine';
import useAppInstallTopBanner from '@/hooks/Cuj/useAppInstallTopBanner';
import useGetMagazineViewModel from '@/hooks/magazine/useGetMagazineViewModel';
import theme from '@/styles/theme';
import { ClientType, CustomPageProps } from '@/types/app';
import { ROUTES } from '@/types/common';
import { MagazineType } from '@/types/magazine';

interface Props extends CustomPageProps {
  viewModel: ReturnType<typeof useGetMagazineViewModel>;
  topBannerViewModel: ReturnType<typeof useAppInstallTopBanner>;
}
const MagazineTemplate = ({ client, viewModel, topBannerViewModel }: Props) => {
  if (viewModel.magazinesIsLoading || viewModel.faqsIsLoading) {
    return <MagazineSkeleton />;
  }

  return (
    <TemplateLayout.Wrapper>
      {client.isWebView && (
        <TemplateLayout.Header useSafeArea>
          <StickyAppHeader title='건강 매거진' />
        </TemplateLayout.Header>
      )}
      {client.clientType === ClientType.MOBILE_WEB && topBannerViewModel.isVisible && (
        <AppInstallTopBanner
          onClickClose={topBannerViewModel.hideBanner}
          content='web_top_bnr_main'
          trackingInfo={{
            campaign: 'web_content_magazine',
          }}
        />
      )}
      <TemplateLayout.Content>
        <Wrapper>
          {!client.isWebView && (
            <BreadCrumbsWrapper>
              <BreadCrumbs visible name='건강매거진' type='magazine' path={`${ROUTES.MAGAZINE.HOME.path}`} />
            </BreadCrumbsWrapper>
          )}
          <Content>
            <PageTile>건강 매거진</PageTile>
            <MagazineWrapper>
              <p>최신 콘텐츠</p>
              <MagazineList>
                {viewModel.magazines.map((magazine) => (
                  <LinkTag
                    key={magazine.magazinePid}
                    href={`${ROUTES.MAGAZINE.DETAIL.path}/${magazine.magazinePid}`}
                    onClickNavigateToScreen={() => viewModel.onClickMove(magazine.magazinePid, MagazineType.ARTICLE)}
                  >
                    <ClickEventLogger
                      eventName={MAGAZINE_LOG_EVENT.MAGAZINE_ITEM_CLICK}
                      pageName={viewModel.pageName}
                      payload={{
                        magazinePid: magazine.magazinePid,
                        section_name: MagazineType.INTERNAL,
                      }}
                    >
                      <ListMagazineLine magazine={magazine} />
                    </ClickEventLogger>
                  </LinkTag>
                ))}
              </MagazineList>
              <ButtonWrapper role='button' onClick={() => viewModel.onClickAllList(MagazineType.INTERNAL)}>
                전체 보기 <Arrow />
              </ButtonWrapper>
            </MagazineWrapper>
            <Dash />
            {viewModel.faqs && viewModel.faqs?.length > 0 && (
              <>
                <MagazineWrapper>
                  <p>1분 FAQ</p>
                  <MagazineList>
                    {viewModel.faqs.map((faq) => (
                      <LinkTag
                        key={faq.magazinePid}
                        href={`${ROUTES.FAQ.DETAIL.path}/${faq.magazinePid}`}
                        onClickNavigateToScreen={() => viewModel.onClickMove(faq.magazinePid, MagazineType.FAQ)}
                      >
                        <ClickEventLogger
                          eventName={MAGAZINE_LOG_EVENT.MAGAZINE_ITEM_CLICK}
                          pageName={viewModel.pageName}
                          payload={{
                            magazinePid: faq.magazinePid,
                            section_name: MagazineType.INTERNAL,
                          }}
                        >
                          <ListMagazineLine
                            magazine={{
                              title: faq.title,
                              mobileTitle: faq.title,
                              image: '',
                              date: faq.date,
                              magazinePid: faq.magazinePid,
                              description: faq.description,
                              mobileDescription: faq.description,
                              siteName: faq.siteName,
                            }}
                          />
                        </ClickEventLogger>
                      </LinkTag>
                    ))}
                  </MagazineList>
                  <ButtonWrapper role='button' onClick={() => viewModel.onClickAllList(MagazineType.FAQ)}>
                    전체 보기 <Arrow />
                  </ButtonWrapper>
                </MagazineWrapper>
              </>
            )}
          </Content>
        </Wrapper>
      </TemplateLayout.Content>
    </TemplateLayout.Wrapper>
  );
};

export default MagazineTemplate;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  background-color: ${({ theme }) => theme.color.white};
`;

const BreadCrumbsWrapper = styled(BreadCrumbs.Wrapper)``;

const Content = styled.main`
  display: flex;
  width: 100%;
  max-width: 1152px;
  flex-direction: column;
`;

const PageTile = styled.div`
  padding: 16px 24px 8px 24px;
  color: ${theme.color.gray700};
  ${theme.typography.UI28SB};
`;

const MagazineWrapper = styled.div`
  width: 100%;
  & > p {
    padding: 16px 24px 8px 24px;
    color: ${theme.color.gray700};
    ${theme.typography.UI22SB};
  }
`;

const MagazineList = styled.ul``;

const ButtonWrapper = styled.div`
  width: 100%;
  border-top: 1px solid ${theme.color.gray40};
  display: flex;
  justify-content: center;
  align-items: center;
  height: 56px;
  color: ${theme.color.gray600};
  ${theme.typography.UI16M};

  & > svg {
    margin-left: 8px;
  }
`;

const Dash = styled.div`
  width: 100%;
  height: 16px;
  background-color: ${theme.color.gray50};
`;
